"use client";

import { useEffect, useState } from "react";
import { copyShareCard, downloadShareCard, type ShareMetric } from "@/lib/atsa/shareCard";
import { logAttempt } from "@/lib/atsa/progress";

type ProgressRecord = {
  sectionSlug: string;
  score: number;
  scoreLabel: string;
  mode?: string;
  detail?: Record<string, string | number>;
};

export function Results({
  title = "Test Complete",
  section,
  metrics,
  shareMetrics,
  record,
  onAgain,
  onOptions,
}: {
  title?: string;
  section?: string;
  metrics: Array<{ label: string; value: React.ReactNode }>;
  shareMetrics?: ShareMetric[];
  record?: ProgressRecord;
  onAgain: () => void;
  onOptions: () => void;
}) {
  const [status, setStatus] = useState("");

  // Log this completed attempt once, when the result screen mounts.
  useEffect(() => {
    if (record) {
      logAttempt({
        section: section ?? "ATSA",
        sectionSlug: record.sectionSlug,
        score: record.score,
        scoreLabel: record.scoreLabel,
        mode: record.mode,
        detail: record.detail,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardMetrics: ShareMetric[] =
    shareMetrics ??
    metrics
      .filter((metric) => typeof metric.value === "string" || typeof metric.value === "number")
      .map((metric) => ({ label: metric.label, value: String(metric.value) }));

  async function handleDownload() {
    setStatus("Preparing image…");
    try {
      await downloadShareCard({ title, section, metrics: cardMetrics });
      setStatus("Image downloaded.");
    } catch {
      setStatus("Could not create the image in this browser.");
    }
  }

  async function handleCopy() {
    setStatus("Copying…");
    const ok = await copyShareCard({ title, section, metrics: cardMetrics });
    setStatus(ok ? "Image copied to clipboard." : "Copy is not supported here. Use Download instead.");
  }

  return (
    <section className="panel result-panel">
      <p className="eyebrow">RESULTS</p>
      <h2>{title}</h2>
      <div className="metric-grid">
        {metrics.map((metric) => (
          <div className="metric" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </div>
      <div className="button-row center">
        <button className="primary-button" onClick={onAgain}>Play Again</button>
        <button className="secondary-button" onClick={onOptions}>Change Options</button>
      </div>
      <div className="share-row">
        <button className="share-button" onClick={handleDownload} aria-label="Download result image">⤓ Download image</button>
        <button className="share-button" onClick={handleCopy} aria-label="Copy result image">⧉ Copy image</button>
      </div>
      <p className="share-status" aria-live="polite">{status}</p>
      {record ? <p className="share-status" style={{ color: "var(--muted)" }}>Saved to your progress.</p> : null}
    </section>
  );
}
